import CrossIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { PUBLIC_CHALLENGE, PUBLIC_SOLUTION } from '../../../api';
import axios from '../../../axios';
import * as OpenReportingCreators from '../../../creators/OpenReporting.creators';
import i18next from '../../../i18n';
import * as Types from '../../../types';
import {
  Challenge,
  ChallengeInput,
  Container,
  IconContainer,
  StyledText,
} from './SpamChallenge.styles';

type SpamChallengeProps = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps;

const SpamChallenge: FC<SpamChallengeProps> = ({
  organisation = '',
  solutionToken = '',
  challenge,
  userAnswer = '',
  setSolutionToken,
  setFetchedChallenge,
  setUserAnswer,
}) => {
  const { t } = useTranslation();
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [timer, setTimer] = useState<NodeJS.Timeout>();
  const [isTyping, setTyping] = useState(false);

  const resetTimer = (input: ChangeEvent<HTMLInputElement>) => {
    setUserAnswer(input.currentTarget.value);
    setTyping(true);
    setError(undefined);
    if (timer) {
      clearTimeout(timer);
    }
    const timeout = setTimeout(() => {
      setTyping(false);
    }, 300);
    setTimer(timeout);
  };

  const fetchChallenge = async () => {
    setFetching(true);
    setError(undefined);
    setUserAnswer('');
    setSolutionToken('');
    try {
      const {
        data: { data },
      } = await axios.get(PUBLIC_CHALLENGE(organisation));
      setFetchedChallenge(data);
      setFetching(false);
    } catch (err: unknown) {
      setError(err as Error);
      setFetching(false);
    }
  };

  const handleRegenerateChallenge = () => {
    fetchChallenge();
  };

  useEffect(() => {
    if (!challenge?.challenge.length) {
      fetchChallenge();
    } else {
      setFetching(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisation, challenge]);

  useEffect(() => {
    const solution = async () => {
      try {
        const {
          data: { token },
        } = await axios.put(PUBLIC_SOLUTION, {
          reference: challenge?.reference ?? '',
          userAnswer: parseInt(userAnswer, 10),
        });
        await setSolutionToken(token);
      } catch (err) {
        setError((err as { response: { data: Error } }).response.data);
      }
    };
    if (!isTyping && userAnswer.length && !solutionToken) {
      solution();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge?.reference, isTyping, solutionToken, userAnswer]);

  if (fetching) return null;

  return (
    <Container>
      <StyledText>{t('public.SpamPuzzle.dialog')}</StyledText>
      <Challenge dir="ltr">
        <p data-testid="public-reporting-challenge-p">{challenge?.challenge}</p>
        <IconButton
          data-testid="public-reporting-challenge-refresh"
          onClick={handleRegenerateChallenge}
          disabled={fetching || solutionToken?.length > 0}
          size="small"
        >
          <RefreshIcon fontSize="small" />
        </IconButton>
        <p> = </p>
        <ChallengeInput
          data-testid="public-reporting-challenge-input"
          defaultValue={userAnswer}
          onChange={resetTimer}
          disabled={solutionToken?.length > 0}
          variant="outlined"
          required
          error={error && !fetching}
          margin="dense"
          type="number"
          direction={i18next.dir?.()}
          style={{ marginTop: 0, marginRight: 5, marginBottom: 0 }}
          inputProps={{ style: { textAlign: 'center', marginTop: 0 } }}
        ></ChallengeInput>

        <IconContainer>
          <Icon>
            {solutionToken.length === 0 && !fetching && error && (
              <CrossIcon
                data-testid="public-reporting-challenge-wrong"
                color="error"
              />
            )}
            {userAnswer.length > 0 && solutionToken.length > 0 && !fetching && (
              <CheckIcon
                data-testid="public-reporting-challenge-correct"
                color="primary"
              />
            )}
          </Icon>
        </IconContainer>
      </Challenge>
    </Container>
  );
};

const mapStateToProps = (state: Types.State) => {
  const openReporting = {
    changePasswordValues: state.openReporting.changePasswordReducer,
    publicReport: state.openReporting.openReportReducer,
    ...state.openReporting?.openReportingReducer,
  };

  return {
    organisation: openReporting.publicReport?.organisation,
    solutionToken: openReporting.solutionToken,
    challenge: openReporting.challenge,
    userAnswer: openReporting.userAnswer,
  };
};

const mapDispatchToProps = {
  setSolutionToken: OpenReportingCreators.setSolutionToken,
  setFetchedChallenge: OpenReportingCreators.setFetchedChallenge,
  setUserAnswer: OpenReportingCreators.setUserAnswer,
};

export default connect(mapStateToProps, mapDispatchToProps)(SpamChallenge);
